import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output } from "@angular/core";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";

import { LuxonDateAdapter } from "@angular/material-luxon-adapter";
import { MatDatepickerInputEvent } from "@angular/material/datepicker";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { DateTime } from "luxon";
import { NgChanges } from "../../model/ng-changes.model";

const clareDateInput = "LLL dd, yyyy";
const clareMonthYearLabel = "MMMM yyyy";

export const CLARE_DATE_PICKER_FORMATS = {
    parse: {
        dateInput: clareDateInput,
    },
    display: {
        dateInput: clareDateInput,
        monthYearLabel: clareMonthYearLabel,
        dateA11yLabel: clareDateInput,
        monthYearA11yLabel: clareMonthYearLabel,
    },
};

@Component({
    selector: "clare-date-picker",
    templateUrl: "./date-picker.component.html",
    styleUrls: ["./date-picker.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: DateAdapter, useClass: LuxonDateAdapter, deps: [MAT_DATE_LOCALE] },
        {
            provide: MAT_DATE_FORMATS,
            useValue: CLARE_DATE_PICKER_FORMATS,
        },
    ],
})
export class DatePickerComponent implements OnInit, OnChanges {
    @Input() date: number = null;
    @Input() min: number = null;
    @Input() max: number = null;
    @Input() label = $localize`:@@SHARED.TEXT.DATE:Date`;
    @Output() dateUpdated = new EventEmitter();

    @Input() private timezone: string = null;
    @Input() private shouldReturnEndOfDay = false;
    @Input() appearance: "fill" | "outline" = null;

    public dateTime: DateTime;
    public minString: string;
    public maxString: string;

    constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
        iconRegistry.addSvgIcon("schedule-calendar", sanitizer.bypassSecurityTrustResourceUrl("assets/images/calendar.svg"));
    }

    private fromMillisWithZone(millis: number) {
        return DateTime.fromMillis(millis, { zone: this.timezone });
    }

    ngOnInit() {
        const theDate = this.date ?? this.min ?? DateTime.now().toMillis();
        this.dateTime = this.fromMillisWithZone(theDate);
    }

    ngOnChanges(changes: NgChanges<DatePickerComponent>) {
        if (changes.date) {
            this.dateTime = changes.date.currentValue ? this.fromMillisWithZone(changes.date.currentValue) : null;
        }
        if (changes.max?.currentValue) {
            this.maxString = this.fromMillisWithZone(changes.max.currentValue).toISODate();
        }
        if (changes.min?.currentValue) {
            this.minString = this.fromMillisWithZone(changes.min.currentValue).toISODate();
        }
    }

    onDateChange(event: MatDatepickerInputEvent<DateTime>) {
        this.date = this.shouldReturnEndOfDay ? event.value.endOf("day").toMillis() : event.value.toMillis();
        this.dateUpdated.emit(this.date);
    }
}
